/**
 * studioExport.service.js
 *
 * True binary export helpers for Studio phase deliverables.
 * - PDF: jsPDF binary generation
 * - DOCX: OOXML .docx binary generation (docx + file-saver)
 */

import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
import {
  Document,
  Packer,
  Paragraph,
  HeadingLevel,
  TextRun,
  ExternalHyperlink,
} from 'docx';

const splitLines = (text = '') => String(text || '').replace(/\r\n/g, '\n').split('\n');

const sanitizeBaseName = (name = 'studio_document') =>
  String(name || 'studio_document')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'studio_document';

const toTitleCase = (value = '') => String(value || '')
  .split(' ')
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
  .join(' ');

const getDiagramDisplayName = (diagram = {}) => {
  if (diagram.display_name) return String(diagram.display_name);
  const base = String(diagram.name || 'diagram').replace(/\.mmd$/i, '').replace(/[_\-]+/g, ' ').trim();
  return toTitleCase(base || 'diagram');
};

const extractMermaidRaw = (content = '') => String(content || '')
  .replace(/```mermaid/gi, '')
  .replace(/```/g, '')
  .trim();

const toBase64Url = (input = '') => {
  const bytes = new TextEncoder().encode(String(input || ''));
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const getDiagramImageCandidates = (diagram = {}) => {
  const urls = [];

  if (Array.isArray(diagram.image_urls)) {
    urls.push(...diagram.image_urls);
  }

  if (diagram.image_url) {
    urls.push(diagram.image_url);
  }

  const raw = extractMermaidRaw(diagram.content);
  if (raw) {
    urls.push(`https://mermaid.ink/img/${toBase64Url(raw)}`);
    urls.push(`https://quickchart.io/mermaid?graph=${encodeURIComponent(raw)}`);
  }

  const priority = (url) => {
    const u = String(url || '').toLowerCase();
    if (u.includes('mermaid.ink')) return 0;
    if (u.includes('quickchart.io')) return 2;
    return 1;
  };

  const seen = new Set();
  return urls
    .map((u) => String(u || '').trim())
    .filter((u) => /^https?:\/\//i.test(u))
    .sort((a, b) => priority(a) - priority(b))
    .filter((u) => {
      if (seen.has(u)) return false;
      seen.add(u);
      return true;
    });
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const fetchFirstImageDataUrl = async (urls = []) => {
  for (const url of urls) {
    try {
      const res = await fetch(url, { method: 'GET', mode: 'cors' });
      if (!res.ok) continue;
      const contentType = (res.headers.get('content-type') || '').toLowerCase();
      if (!contentType.startsWith('image/')) continue;
      const blob = await res.blob();
      const dataUrl = await blobToDataUrl(blob);
      if (typeof dataUrl === 'string' && dataUrl.startsWith('data:image/')) {
        return { dataUrl, source: url };
      }
    } catch {
      // Try next candidate URL.
    }
  }
  return null;
};

const imageFormatFromDataUrl = (dataUrl = '') => {
  if (String(dataUrl).startsWith('data:image/jpeg') || String(dataUrl).startsWith('data:image/jpg')) {
    return 'JPEG';
  }
  return 'PNG';
};

const drawPdfWrappedLines = (pdf, lines, marginX, ensureSpace, yState, lineHeight = 12) => {
  for (const line of lines) {
    ensureSpace(lineHeight);
    pdf.text(line, marginX, yState.value);
    yState.value += lineHeight;
  }
};

const parseMarkdownLinkLine = (line = '') => {
  const trimmed = String(line || '').trim();
  const match = trimmed.match(/^(?:-\s*)?\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/i);
  if (!match) return null;
  return {
    label: match[1],
    url: match[2],
    isBullet: /^-\s*/.test(trimmed),
  };
};

const parseMarkdownImageLine = (line = '') => {
  const trimmed = String(line || '').trim();
  const match = trimmed.match(/^!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)$/i);
  if (!match) return null;
  return {
    alt: match[1] || 'diagram',
    url: match[2],
  };
};

export const exportStudioMarkdownAsPdf = async (docArtifact = {}, diagramArtifacts = []) => {
  const title = docArtifact.title || 'Studio Document';
  const content = docArtifact.content || '';

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 48;
  const maxWidth = pageWidth - marginX * 2;
  const yState = { value: 58 };

  const ensureSpace = (needed = 16) => {
    if (yState.value + needed > pageHeight - 40) {
      pdf.addPage();
      yState.value = 48;
    }
  };

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  const titleLines = pdf.splitTextToSize(title, maxWidth);
  titleLines.forEach((line) => {
    ensureSpace(24);
    pdf.text(line, marginX, yState.value);
    yState.value += 24;
  });

  yState.value += 8;

  for (const line of splitLines(content)) {
    const trimmed = line.trim();

    const linkLine = parseMarkdownLinkLine(trimmed);
    if (linkLine) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      ensureSpace(16);
      const label = `${linkLine.isBullet ? '• ' : ''}${linkLine.label}`;
      pdf.textWithLink(label, marginX, yState.value, { url: linkLine.url });
      yState.value += 16;
      continue;
    }

    const imageLine = parseMarkdownImageLine(trimmed);
    if (imageLine) {
      // Inline image rendering for markdown image references.
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      ensureSpace(16);
      pdf.textWithLink(`Open image: ${imageLine.alt}`, marginX, yState.value, { url: imageLine.url });
      yState.value += 16;
      continue;
    }

    if (!trimmed) {
      yState.value += 8;
      continue;
    }

    if (trimmed.startsWith('### ')) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      const wrapped = pdf.splitTextToSize(trimmed.replace(/^###\s+/, ''), maxWidth);
      wrapped.forEach((w) => {
        ensureSpace(18);
        pdf.text(w, marginX, yState.value);
        yState.value += 18;
      });
      continue;
    }

    if (trimmed.startsWith('## ')) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      const wrapped = pdf.splitTextToSize(trimmed.replace(/^##\s+/, ''), maxWidth);
      wrapped.forEach((w) => {
        ensureSpace(20);
        pdf.text(w, marginX, yState.value);
        yState.value += 20;
      });
      continue;
    }

    if (trimmed.startsWith('# ')) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      const wrapped = pdf.splitTextToSize(trimmed.replace(/^#\s+/, ''), maxWidth);
      wrapped.forEach((w) => {
        ensureSpace(22);
        pdf.text(w, marginX, yState.value);
        yState.value += 22;
      });
      continue;
    }

    if (trimmed.startsWith('- ')) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      const wrapped = pdf.splitTextToSize(`• ${trimmed.slice(2)}`, maxWidth - 8);
      wrapped.forEach((w, idx) => {
        ensureSpace(16);
        pdf.text(w, marginX + (idx === 0 ? 0 : 10), yState.value);
        yState.value += 16;
      });
      continue;
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    const wrapped = pdf.splitTextToSize(trimmed, maxWidth);
    wrapped.forEach((w) => {
      ensureSpace(16);
      pdf.text(w, marginX, yState.value);
      yState.value += 16;
    });
  }

  const diagrams = Array.isArray(diagramArtifacts)
    ? diagramArtifacts.filter((d) => d && (d.content || d.image_url || (Array.isArray(d.image_urls) && d.image_urls.length)))
    : [];

  if (diagrams.length > 0) {
    ensureSpace(30);
    yState.value += 8;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(15);
    pdf.text('Design Diagrams', marginX, yState.value);
    yState.value += 18;

    for (const diagram of diagrams) {
      const name = getDiagramDisplayName(diagram);
      const candidates = getDiagramImageCandidates(diagram);

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      ensureSpace(18);
      pdf.text(name, marginX, yState.value);
      yState.value += 14;

      const loadedImage = await fetchFirstImageDataUrl(candidates);
      if (loadedImage?.dataUrl) {
        try {
          const format = imageFormatFromDataUrl(loadedImage.dataUrl);
          const props = pdf.getImageProperties(loadedImage.dataUrl);
          const maxImgWidth = maxWidth;
          const maxImgHeight = 240;

          let renderW = maxImgWidth;
          let renderH = (props.height * renderW) / props.width;
          if (renderH > maxImgHeight) {
            renderH = maxImgHeight;
            renderW = (props.width * renderH) / props.height;
          }

          ensureSpace(renderH + 24);
          pdf.addImage(loadedImage.dataUrl, format, marginX, yState.value, renderW, renderH, undefined, 'FAST');
          yState.value += renderH + 8;

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          ensureSpace(12);
          pdf.textWithLink('Open Diagram Image', marginX, yState.value, { url: loadedImage.source });
          yState.value += 12;
          yState.value += 6;
          continue;
        } catch {
          // Fall back to text representation below.
        }
      }

      if (candidates.length > 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        ensureSpace(13);
        pdf.textWithLink('Open Diagram Image', marginX, yState.value, { url: candidates[0] });
        yState.value += 13;
      }

      const raw = extractMermaidRaw(diagram.content);
      if (raw) {
        pdf.setFont('courier', 'normal');
        pdf.setFontSize(9);
        const codeLines = pdf.splitTextToSize(raw, maxWidth);
        drawPdfWrappedLines(pdf, codeLines.slice(0, 50), marginX, ensureSpace, yState, 11);
      }

      yState.value += 8;
    }
  }

  const fileBase = sanitizeBaseName(docArtifact.file_name || title);
  pdf.save(`${fileBase.replace(/\.md$/i, '')}.pdf`);
};

export const exportStudioMarkdownAsDocx = async (docArtifact = {}, diagramArtifacts = []) => {
  const title = docArtifact.title || 'Studio Document';
  const content = docArtifact.content || '';

  const children = [
    new Paragraph({
      heading: HeadingLevel.TITLE,
      children: [new TextRun({ text: title, bold: true })],
    }),
  ];

  splitLines(content).forEach((line) => {
    const trimmed = line.trim();

    const linkLine = parseMarkdownLinkLine(trimmed);
    if (linkLine) {
      const linkNode = new ExternalHyperlink({
        children: [
          new TextRun({
            text: `${linkLine.isBullet ? '• ' : ''}${linkLine.label}`,
            style: 'Hyperlink',
          }),
        ],
        link: linkLine.url,
      });
      children.push(new Paragraph({ children: [linkNode] }));
      return;
    }

    const imageLine = parseMarkdownImageLine(trimmed);
    if (imageLine) {
      const imageLink = new ExternalHyperlink({
        children: [new TextRun({ text: `Open image: ${imageLine.alt}`, style: 'Hyperlink' })],
        link: imageLine.url,
      });
      children.push(new Paragraph({ children: [imageLink] }));
      return;
    }

    if (!trimmed) {
      children.push(new Paragraph({ text: '' }));
      return;
    }

    if (trimmed.startsWith('### ')) {
      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_3,
        children: [new TextRun(trimmed.replace(/^###\s+/, ''))],
      }));
      return;
    }

    if (trimmed.startsWith('## ')) {
      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_2,
        children: [new TextRun(trimmed.replace(/^##\s+/, ''))],
      }));
      return;
    }

    if (trimmed.startsWith('# ')) {
      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun(trimmed.replace(/^#\s+/, ''))],
      }));
      return;
    }

    if (trimmed.startsWith('- ')) {
      children.push(new Paragraph({
        text: trimmed.slice(2),
        bullet: { level: 0 },
      }));
      return;
    }

    children.push(new Paragraph({ text: trimmed }));
  });

  const diagrams = Array.isArray(diagramArtifacts)
    ? diagramArtifacts.filter((d) => d && (d.content || d.image_url || (Array.isArray(d.image_urls) && d.image_urls.length)))
    : [];

  if (diagrams.length > 0) {
    children.push(new Paragraph({ text: '' }));
    children.push(new Paragraph({
      heading: HeadingLevel.HEADING_2,
      children: [new TextRun('Design Diagrams')],
    }));

    diagrams.forEach((diagram) => {
      const name = getDiagramDisplayName(diagram);
      const candidates = getDiagramImageCandidates(diagram);
      const source = candidates[0] || '';
      const raw = extractMermaidRaw(diagram.content);

      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_3,
        children: [new TextRun(name)],
      }));

      if (source) {
        const externalLink = new ExternalHyperlink({
          children: [new TextRun({ text: 'Open Diagram Image', style: 'Hyperlink' })],
          link: source,
        });
        children.push(new Paragraph({ children: [externalLink] }));
      }

      if (raw) {
        children.push(new Paragraph({ text: raw }));
      }
    });
  }

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);
  const fileBase = sanitizeBaseName(docArtifact.file_name || title);
  saveAs(blob, `${fileBase.replace(/\.md$/i, '')}.docx`);
};
